import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Keeps the product-page quantity selector synchronized with the selected
 * variant's quantity in the Shopify cart.
 */
class ProductCartQuantity extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  #syncedQuantity = 0;
  #desiredQuantity = 0;
  #syncing = false;

  /** @type {number | undefined} */
  #syncTimer;

  /** @type {AbortController | null} */
  #requestController = null;

  #syncGeneration = 0;

  connectedCallback() {
    super.connectedCallback();

    const initialQuantity = Number(this.dataset.cartQuantity || this.quantityInput.value) || 0;
    this.#syncedQuantity = initialQuantity;
    this.#desiredQuantity = initialQuantity;
    this.#updateCheckoutQuantity(initialQuantity);

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    this.closest('.shopify-section')?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleExternalCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    window.clearTimeout(this.#syncTimer);
    this.#cancelPendingRequest();
  }

  #handleQuantityUpdate = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const quantity = Math.max(0, Number(event.detail?.quantity) || 0);
    this.#desiredQuantity = quantity;
    this.#updateCheckoutQuantity(quantity);
    this.#scheduleCartSync();
  };

  #scheduleCartSync() {
    window.clearTimeout(this.#syncTimer);
    this.#syncTimer = window.setTimeout(() => {
      this.#syncTimer = undefined;
      void this.#syncCart();
    }, 120);
  }

  #handleVariantUpdate = (event) => {
    const variantId = event.detail?.resource?.id;
    if (!variantId) return;

    const updatedSelector = event.detail?.data?.html?.querySelector('product-cart-quantity');
    const cartQuantity = Number(updatedSelector?.dataset.cartQuantity) || 0;
    const checkoutMinimum = Number(updatedSelector?.dataset.checkoutMinimum) || 1;

    this.#cancelPendingRequest();
    this.dataset.variantId = String(variantId);
    this.dataset.cartQuantity = String(cartQuantity);
    this.dataset.checkoutMinimum = String(checkoutMinimum);
    window.clearTimeout(this.#syncTimer);
    this.#syncedQuantity = cartQuantity;
    this.#desiredQuantity = cartQuantity;
    this.quantityInput.value = String(cartQuantity);
    this.#updateCheckoutQuantity(cartQuantity);
  };

  #handleExternalCartUpdate = (event) => {
    if (event.target === this || this.#syncing) return;

    const cart = event.detail?.resource;
    if (!Array.isArray(cart?.items)) return;

    const cartQuantity = this.#getVariantQuantity(cart);
    window.clearTimeout(this.#syncTimer);
    this.#syncedQuantity = cartQuantity;
    this.#desiredQuantity = cartQuantity;
    this.dataset.cartQuantity = String(cartQuantity);
    this.quantityInput.value = String(cartQuantity);
    this.#updateCheckoutQuantity(cartQuantity);
  };

  #cancelPendingRequest() {
    this.#syncGeneration += 1;
    this.#requestController?.abort();
    this.#requestController = null;
  }

  async #syncCart() {
    if (this.#syncing) return;

    const syncGeneration = this.#syncGeneration;
    this.#syncing = true;
    this.setAttribute('aria-busy', 'true');

    try {
      while (this.#desiredQuantity !== this.#syncedQuantity) {
        const requestedQuantity = this.#desiredQuantity;
        const variantId = this.dataset.variantId;
        if (!variantId) throw new Error('Missing product variant ID.');

        const requestController = new AbortController();
        this.#requestController?.abort();
        this.#requestController = requestController;

        const cart = await this.#requestCartQuantity(requestedQuantity, variantId, requestController.signal);
        if (
          requestController.signal.aborted ||
          syncGeneration !== this.#syncGeneration ||
          !this.isConnected ||
          this.dataset.variantId !== variantId
        ) {
          return;
        }

        const cartQuantity = this.#getVariantQuantity(cart, variantId);

        this.#syncedQuantity = cartQuantity;
        this.dataset.cartQuantity = String(cartQuantity);

        if (this.#desiredQuantity === requestedQuantity) {
          this.#desiredQuantity = cartQuantity;
          this.quantityInput.value = String(cartQuantity);
          this.#updateCheckoutQuantity(cartQuantity);
        }

        this.dispatchEvent(
          new CartUpdateEvent(cart, `product-cart-quantity-${variantId}`, {
            source: 'product-cart-quantity',
            itemCount: Number(cart.item_count) || 0,
            variantId,
            sections: cart.sections || {},
          })
        );
      }
    } catch (error) {
      if (
        (error instanceof DOMException && error.name === 'AbortError') ||
        syncGeneration !== this.#syncGeneration ||
        !this.isConnected
      ) {
        return;
      }

      console.error(error);
      this.#desiredQuantity = this.#syncedQuantity;
      this.quantityInput.value = String(this.#syncedQuantity);
      this.#updateCheckoutQuantity(this.#syncedQuantity);

      const liveRegion = this.closest('product-form-component')?.querySelector('[ref="liveRegion"]');
      if (liveRegion instanceof HTMLElement) {
        liveRegion.textContent = error instanceof Error ? error.message : String(error);
      }
    } finally {
      if (syncGeneration === this.#syncGeneration) {
        this.#requestController = null;
      }
      this.#syncing = false;
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {number} quantity
   * @param {string} variantId
   * @param {AbortSignal} signal
   */
  async #requestCartQuantity(quantity, variantId, signal) {
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : undefined))
      .filter(Boolean);

    const sectionData =
      sectionIds.length > 0
        ? {
            sections: [...new Set(sectionIds)].join(','),
            sections_url: window.location.pathname,
          }
        : {};

    const isFirstAddition = this.#syncedQuantity === 0 && quantity > 0;
    const body = isFirstAddition
      ? { items: [{ id: Number(variantId), quantity }], ...sectionData }
      : { id: variantId, quantity, ...sectionData };
    const route = isFirstAddition ? Theme.routes.cart_add_url : Theme.routes.cart_change_url;
    const response = await fetch(route, { ...fetchConfig('json', { body: JSON.stringify(body) }), signal });
    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Unable to update the cart.');
    }

    if (typeof data.item_count === 'number') return data;

    const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      signal,
    });
    const cart = await cartResponse.json();

    if (!cartResponse.ok) {
      throw new Error('Unable to refresh the cart.');
    }

    cart.sections = data.sections || {};
    return cart;
  }

  #getVariantQuantity(cart, variantId = String(this.dataset.variantId)) {
    return cart.items
      .filter((item) => String(item.variant_id) === variantId)
      .reduce((total, item) => total + Number(item.quantity || 0), 0);
  }

  #updateCheckoutQuantity(cartQuantity) {
    const checkoutMinimum = Number(this.dataset.checkoutMinimum) || 1;
    this.checkoutQuantityInput.value = String(Math.max(cartQuantity, checkoutMinimum));
  }

  get quantityInput() {
    const input = this.querySelector('quantity-selector-component input[type="number"]');
    if (!(input instanceof HTMLInputElement)) throw new Error('Missing product quantity input.');
    return input;
  }

  get checkoutQuantityInput() {
    const input = this.querySelector('[data-cart-sync-checkout-quantity]');
    if (!(input instanceof HTMLInputElement)) throw new Error('Missing checkout quantity input.');
    return input;
  }
}

if (!customElements.get('product-cart-quantity')) {
  customElements.define('product-cart-quantity', ProductCartQuantity);
}
